// src/components/RealEstateLandRegister.tsx

import React from 'react';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import {
  ILandParcelEntry,
  IRealEstate,
} from '../../../../../../Interfaces/IRealEstate';
import { ICourt } from '../../../../../../Interfaces/IAemter';
import DistrictCourtSearch from '../../../../../generic/DistrictCourtSearch';
import { ILegalTransactionRelationshipRealEstate } from '../../../../../../Interfaces/ILegalTransactionRelationship/Relationships/RealEstate/ILegalTransactionRelationshipRealEstate';

interface IProps {
  /** Das vollständige Objekt aus dem Parent‐State */
  currentObject: IRealEstate | ILegalTransactionRelationshipRealEstate;
  /** Funktion, um das aktualisierte Objekt in den Parent zurückzugeben */
  setCurrentObject: Function;
  /** Optionaler Abstand oben */
  marginTop?: number;
}

export const RealEstateLandRegister: React.FC<IProps> = ({
  currentObject,
  setCurrentObject,
  marginTop,
}) => {
  // Wenn im Parent schon Einträge vorhanden sind, initialisiere lokal damit,
  // ansonsten bei Mount einen leeren Eintrag anlegen.
  //const [eintraege, setEintraege] = useState<ILandParcelEntry[]>([]);
  const eintraege =
    currentObject.LandParcelEntryArray.length > 0
      ? currentObject.LandParcelEntryArray
      : [
          {
            idLandRegister: undefined,
            idCourt: null,
            Court: null,
            LandRegister: '',
            SheetLandRegister: '',
            Parcel: '',
            LandParcel: '',
          },
        ];

  const setEintraege = (updatedEintrage: ILandParcelEntry[]) => {
    setCurrentObject({
      ...currentObject,
      LandParcelEntryArray: updatedEintrage,
    });
  };

  /** Erzeugt einen neuen, leeren Grundbuch‐Eintrag */
  const createEmptyEintrag = (): ILandParcelEntry => ({
    idLandRegister: undefined,
    idCourt: null,
    Court: null,
    LandRegister: '',
    SheetLandRegister: '',
    Parcel: '',
    LandParcel: '',
  });

  /** Fügt einen neuen leeren Eintrag ans Ende an */
  const handleAddEintrag = () => {
    setEintraege([...eintraege, createEmptyEintrag()]);
  };

  /** Löscht den Eintrag an der Stelle "index" */
  const handleDeleteEintrag = (index: number) => {
    setEintraege([...eintraege.filter((_, i) => i !== index)]);
  };

  /**
   * Ändert ein bestimmtes Feld (field) in einem Eintrag (Zeile index).
   * field ist einer der Keys von GrundbuchEintrag (außer idLandRegister).
   */
  const handleChangeField = (
    index: number,
    field: keyof Omit<ILandParcelEntry, 'idLandRegister'>,
    value: string | number | ICourt | null
  ) => {
    setEintraege(
      eintraege.map((eintrag, i) =>
        i !== index
          ? eintrag
          : {
              ...eintrag,
              [field]: value,
            }
      )
    );
  };

  /**
   * Spezieller Handler, wenn DistrictCourtSearch ein neues Court‐Objekt liefert.
   * Wir merken uns sowohl idCourt als auch das komplette Court‐Objekt.
   */
  const handleCourtChange = (
    index: number,
    courtObj: ICourt | null | undefined
  ) => {
    const idCourt = courtObj?.idCourt ?? null;
    setEintraege(
      eintraege.map((eintrag, i) =>
        i !== index
          ? eintrag
          : {
              ...eintrag,
              idCourt,
              Court: courtObj ?? null,
            }
      )
    );
  };

  return (
    <>
      <Typography
        variant='h5'
        sx={{ mt: marginTop === undefined ? 2 : marginTop, mb: 2 }}
      >
        Grundbuchinformationen
      </Typography>
      <Typography variant='body1' mb={2}>
        Falls Ihnen die Daten über den Grundbucheintrag Ihres Grundstückes
        vorliegen, tragen Sie diese bitte ein. Andernfalls können diese
        Informationen nachgereicht oder durch die Mitarbeiter des Notarbüros
        ermittelt werden.
      </Typography>

      <Box sx={{ width: '100%' }}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Amtsgericht</TableCell>
              <TableCell>Gemarkung</TableCell>
              <TableCell>Flur</TableCell>
              <TableCell>Flurstück</TableCell>
              <TableCell>Blatt</TableCell>
              <TableCell align='center'>Aktion</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {eintraege.map((eintrag, index) => (
              <TableRow key={index}>
                {/* Amtsgericht (DistrictCourtSearch) */}
                <TableCell sx={{ minWidth: 200 }}>
                  <DistrictCourtSearch
                    districtCourtObject={eintrag.Court}
                    setDistrictCourtObject={(court: any) =>
                      handleCourtChange(index, court)
                    }
                    setDistrictCourt={() => {
                      /* Nur Prop‐Signatur, bleibt leer */
                    }}
                  />
                </TableCell>

                {/* LandRegister (Grundbuch) */}
                <TableCell sx={{ minWidth: 150 }}>
                  <TextField
                    value={eintrag.LandRegister}
                    size='small'
                    fullWidth
                    placeholder='Gemarkung'
                    onChange={e =>
                      handleChangeField(index, 'LandRegister', e.target.value)
                    }
                  />
                </TableCell>

                {/* Parcel (Flur) */}
                <TableCell sx={{ minWidth: 80 }}>
                  <TextField
                    value={eintrag.Parcel}
                    size='small'
                    fullWidth
                    placeholder='Flur'
                    onChange={e =>
                      handleChangeField(index, 'Parcel', e.target.value)
                    }
                  />
                </TableCell>

                {/* LandParcel (Flurstück) */}
                <TableCell sx={{ minWidth: 100 }}>
                  <TextField
                    value={eintrag.LandParcel}
                    size='small'
                    fullWidth
                    placeholder='Flurstück'
                    onChange={e =>
                      handleChangeField(index, 'LandParcel', e.target.value)
                    }
                  />
                </TableCell>

                {/* SheetLandRegister (Blatt) */}
                <TableCell sx={{ minWidth: 120 }}>
                  <TextField
                    value={eintrag.SheetLandRegister}
                    size='small'
                    fullWidth
                    placeholder='Blatt'
                    onChange={e =>
                      handleChangeField(
                        index,
                        'SheetLandRegister',
                        e.target.value
                      )
                    }
                  />
                </TableCell>

                {/* Löschen‐Button */}
                <TableCell align='center' sx={{ width: 50 }}>
                  <IconButton
                    size='small'
                    color='error'
                    onClick={() => handleDeleteEintrag(index)}
                  >
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Grid container justifyContent='flex-end' sx={{ mt: 1 }}>
          <Grid item>
            <Button variant='outlined' onClick={handleAddEintrag}>
              + Neuer Grundbuch‐Eintrag
            </Button>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
